import type { CountryCode2 } from '@eversports/fiskaly-api/generated/es-sign/api.generated'

interface TinValidationLimits {
  amount: number
  decimalDigits: number
  currency: string
}

const REQUIRE_TIN_LIMITS = new Map<CountryCode2, TinValidationLimits>([
  ['ES', { amount: 300000, decimalDigits: 2, currency: 'EUR' }], // 3000.00 EUR
  ['IT', { amount: Number.NEGATIVE_INFINITY, decimalDigits: 2, currency: 'EUR' }], // Show for all amounts for Italy
])

interface GetTinValidationLimitsParams {
  countryCode: string
  targetCurrency: string
  isFractionalUnitRequired?: boolean
}

export function getTinLimit({
  countryCode,
  targetCurrency,
  isFractionalUnitRequired = true,
}: GetTinValidationLimitsParams): number | undefined {
  const limit = REQUIRE_TIN_LIMITS.get(countryCode as CountryCode2)

  if (!limit) {
    return undefined
  }

  if (targetCurrency !== limit.currency) {
    throw new Error(`Currency mismatch: expected ${limit.currency}, got ${targetCurrency}`)
  }

  if (isFractionalUnitRequired || limit.amount === Number.NEGATIVE_INFINITY) {
    return limit.amount
  }

  return limit.amount / 10 ** limit.decimalDigits
}

export interface TinValidationRequiredParams extends Omit<GetTinValidationLimitsParams, 'targetCurrency'> {
  isFractionalUnitRequired: boolean
  amount?: {
    value: number
    currency: string
  }
}

export function isTinValidationRequired({
  countryCode,
  amount,
  isFractionalUnitRequired,
}: TinValidationRequiredParams): boolean {
  if (amount?.value === undefined || amount?.value === null) {
    return false
  }

  const tinLimit = getTinLimit({
    countryCode,
    targetCurrency: amount.currency ?? 'EUR',
    isFractionalUnitRequired,
  })

  return tinLimit !== undefined && amount.value >= tinLimit
}

export function preValidateTin(countryCode: string, input: string): boolean {
  let regex: RegExp | null = null

  switch (countryCode) {
    case 'ES':
      regex = /^[a-zA-Z0-9][0-9]{7}[a-zA-Z0-9]$/
      break
    case 'IT':
      // regex = /^([A-Za-z]{6}[0-9lmnpqrstuvLMNPQRSTUV]{2}[abcdehlmprstABCDEHLMPRST]{1}[0-9lmnpqrstuvLMNPQRSTUV]{2}[A-Za-z]{1}[0-9lmnpqrstuvLMNPQRSTUV]{3}[A-Za-z]{1})$|([0-9]{11})$/g
      // We currently use only a simplified version of the Italian fiscal code validation
      // The problem is that tourists would not be able to buy anything, as they don't have a fiscal code
      // We just want to make sure at least the people have a look at the fiscal code field and enter something
      regex = /^[a-zA-Z0-9]{6,}$/
      break

    default:
      return true
  }

  return regex ? regex.test(input) : true
}
